import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { TriangleAlert } from 'lucide-react'
import Card from 'app/frontend/base/components/Card'
import TeamContext from 'app/frontend/base/config/context/team'
import {
  getClass,
  getSpecialization,
} from 'app/frontend/base/services/characterValues'
import RaidPlan from 'app/frontend/types/custom/RaidPlan'
import { RaidPlanOwner } from 'app/frontend/base/api/raid_plans'
import {
  EventPlanOverridesByPlanId,
  getActorOverrideCharacterId,
  getActorOverrideIgnored,
  getActorOverrideReplacementActor,
  getEncounterAssignmentAbilityHidden,
  getPlanActorOverrides,
  getPlanCastTargetOverrides,
  getPlanEncounterAssignmentOverrides,
} from './eventActivityPlanOverrides'
import {
  EventActivityAutoMatchMode,
  resolveEventActivityCastTargets,
  resolveEventActivityTimelineActors,
  resolveEventActivityTimelineAssignments,
  toResolvedParticipants,
} from './eventActivityTimelineResolver'
import {
  buildLinkedPlanAssignments,
  resolveLinkedPlanEncounterAssignmentRows,
  resolveLinkedPlanPhaseDividers,
  resolveLinkedPlanTimelineMaxTime,
} from '../hooks/resolveLinkedPlanEncounterAssignmentRows'
import {
  applyEventActivityPlanOverrides,
  getEventActivityUnresolvedIssues,
} from '../hooks/applyEventActivityPlanOverrides'
import {
  getRaidlistParticipantCharacterId,
  getRaidlistParticipantId,
  ParsedRaidlistParticipant,
} from '../../TeamPlans/components/createNoteStorage'
import { EventActivityParticipantEntry } from './eventActivityParticipantEntry'
import { forceSelectedOnEntries } from '../../TeamPlans/components/default_setup/defaultSetupEntries'
import CharacterRole from 'app/frontend/types/custom/CharacterRole'
import ResolvedPlanPreviewContent from './ResolvedPlanPreviewContent'
import { OverridePopoverLayerContext } from './EventPlanOverridePopover'
import { buildEventActivityLinkedPlanNote } from './eventActivityNoteExport'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { PlanAbilityDefaults } from '../../TeamPlans/components/planAbilityDefaults'

type Props = {
  owner: RaidPlanOwner
  raidPlan: RaidPlan
  planListPathPrefix: string
  parsedParticipants?: ParsedRaidlistParticipant[]
  rosterMemberships?: wowaudit.TeamMembership[]
  participantEntries?: EventActivityParticipantEntry[]
  autoMatchMode?: EventActivityAutoMatchMode
  planOverrides: EventPlanOverridesByPlanId
  stickyActorCharacterIdsByActorUuid?: Record<string, number>
  onResolvedActorIdentityMapChange?: (value: Record<string, string>) => void
  onResolvedNoteChange?: (value: string) => void
  onPlanOverridesChange: (value: EventPlanOverridesByPlanId) => void
  allowUnresolvedActorOverride?: boolean
  onActorPreferredCharacterChange?: (
    actorUuid: string,
    characterId: number,
  ) => void
  planAbilityDefaults?: PlanAbilityDefaults
}

const toCharacterRole = (role: string): CharacterRole => {
  if (role === 'tank') return 'tank'
  if (role === 'heal') return 'heal'
  if (role === 'ranged') return 'ranged'
  return 'melee'
}

const buildCharacterReference = (
  participant: ParsedRaidlistParticipant,
): wowaudit.CharacterReference => {
  const className = getClass(participant.classId.toString()) ?? 'Warrior'
  const specialization =
    participant.specId === null
      ? undefined
      : getSpecialization(participant.specId.toString())
  const role = toCharacterRole(specialization?.default_role ?? 'melee')
  return {
    id: getRaidlistParticipantCharacterId(participant.identity),
    name: participant.name,
    classId: participant.classId,
    characterClass: className ?? 'Warrior',
    role,
    currentSpecId: participant.specId ?? undefined,
    currentSpec: null,
    userId: getRaidlistParticipantId(participant.identity),
    realm: {
      name: participant.realm,
      slug: participant.realm.toLowerCase(),
      id: getRaidlistParticipantId(participant.identity),
      region: 'us',
      timezone: 'UTC',
    },
  } as unknown as wowaudit.CharacterReference
}

const buildTeamMembership = (
  participant: ParsedRaidlistParticipant,
): wowaudit.TeamMembership => {
  const specialization =
    participant.specId === null
      ? undefined
      : getSpecialization(participant.specId.toString())
  return {
    id: getRaidlistParticipantId(participant.identity),
    characterReference: buildCharacterReference(participant),
    role: toCharacterRole(specialization?.default_role ?? 'melee'),
    specId: participant.specId,
  } as wowaudit.TeamMembership
}

const buildParticipantEntries = (
  participants: ParsedRaidlistParticipant[],
  activityId: number,
) => {
  const teamMemberships = participants.map((participant) =>
    buildTeamMembership(participant),
  )
  const entries = teamMemberships.map<EventActivityParticipantEntry>(
    (membership) => {
      const participant = {
        id: membership.id,
        addedAsGuest: false,
        characterId: membership.characterReference.id,
        characterReference: membership.characterReference,
        effectiveDefaultSelectionStatus: true,
        role: membership.role,
        selections: {},
        specId: membership.specId,
        status: 'present',
        teamMembership: membership,
      } as unknown as wowaudit.EventParticipant
      return {
        participant,
        selected: true,
        role: membership.role,
        specId: membership.specId,
        characterId: membership.characterReference.id,
        availableTeamMemberships: teamMemberships,
        rosterCharacterById: Object.fromEntries(
          teamMemberships.map((entry) => [
            entry.characterReference.id,
            entry.characterReference,
          ]),
        ),
        selections: {
          [activityId]: {
            selected: true,
            role: membership.role,
            specId: membership.specId,
            characterId: membership.characterReference.id,
          },
        },
      }
    },
  )
  return { entries, teamMemberships }
}

const buildRosterParticipantEntries = (
  memberships: wowaudit.TeamMembership[],
  activityId: number,
) => {
  const entries = memberships.map<EventActivityParticipantEntry>(
    (membership) => {
      const participant = {
        id: membership.id,
        addedAsGuest: false,
        characterId: membership.characterReference.id,
        characterReference: membership.characterReference,
        effectiveDefaultSelectionStatus: true,
        role: membership.role,
        selections: {},
        specId: membership.specId,
        status: 'present',
        teamMembership: membership,
      } as unknown as wowaudit.EventParticipant
      return {
        participant,
        selected: true,
        role: membership.role,
        specId: membership.specId,
        characterId: membership.characterReference.id,
        availableTeamMemberships: memberships,
        rosterCharacterById: Object.fromEntries(
          memberships.map((entry) => [
            entry.characterReference.id,
            entry.characterReference,
          ]),
        ),
        selections: {
          [activityId]: {
            selected: true,
            role: membership.role,
            specId: membership.specId,
            characterId: membership.characterReference.id,
          },
        },
      }
    },
  )
  return { entries, teamMemberships: memberships }
}

const CreateNotePreview = ({
  owner,
  raidPlan,
  planListPathPrefix,
  parsedParticipants,
  rosterMemberships,
  participantEntries,
  autoMatchMode = 'full',
  planOverrides,
  stickyActorCharacterIdsByActorUuid,
  onResolvedActorIdentityMapChange,
  onResolvedNoteChange,
  onPlanOverridesChange,
  allowUnresolvedActorOverride = false,
  onActorPreferredCharacterChange,
  planAbilityDefaults,
}: Props) => {
  const { t } = useTranslation()
  const {
    data: { currentExpansion },
  } = usePageInfo()
  const activityId = raidPlan.id
  const encounter = useMemo(
    () =>
      currentExpansion.seasons
        .flatMap((season) => season.instances)
        .flatMap((instance) => instance.encounters)
        .find((entry) => entry.id === raidPlan.encounterId),
    [currentExpansion, raidPlan.encounterId],
  )

  const { entries, teamMemberships } = useMemo(() => {
    if (participantEntries) {
      return {
        entries: participantEntries,
        teamMemberships: rosterMemberships ?? [],
      }
    }
    return rosterMemberships
      ? buildRosterParticipantEntries(rosterMemberships, activityId)
      : buildParticipantEntries(parsedParticipants ?? [], activityId)
  }, [activityId, parsedParticipants, participantEntries, rosterMemberships])
  const encounterRows = useMemo(
    () =>
      resolveLinkedPlanEncounterAssignmentRows({
        raidPlan,
        timeline: raidPlan.timeline,
        unknownAbilityLabel: t('dashboard.team.plans.unknown-ability'),
      }),
    [raidPlan, t],
  )
  const phaseDividers = useMemo(
    () =>
      resolveLinkedPlanPhaseDividers({
        raidPlan,
        timeline: raidPlan.timeline,
      }),
    [raidPlan],
  )
  const linkedPlanAssignments = useMemo(
    () =>
      buildLinkedPlanAssignments({
        encounterAssignments: raidPlan.timeline.encounter_assignments,
        encounterRows,
      }),
    [encounterRows, raidPlan.timeline.encounter_assignments],
  )
  const selectedRows = useMemo(
    () => linkedPlanAssignments.map((entry) => entry.row),
    [linkedPlanAssignments],
  )
  const selectedParticipants = useMemo(
    () => toResolvedParticipants(entries, activityId),
    [activityId, entries],
  )
  const overrideParticipants = useMemo(
    () =>
      toResolvedParticipants(
        participantEntries
          ? forceSelectedOnEntries(participantEntries, activityId)
          : entries,
        activityId,
      ),
    [activityId, entries, participantEntries],
  )
  const actorOverrides = useMemo(
    () => getPlanActorOverrides(planOverrides, raidPlan.id),
    [planOverrides, raidPlan.id],
  )
  const resolverResult = useMemo(
    () =>
      resolveEventActivityTimelineAssignments({
        activityId,
        entries,
        encounterRows: selectedRows,
        abilities: linkedPlanAssignments.map((entry) => entry.ability),
        autoMatchMode,
      }),
    [activityId, autoMatchMode, entries, linkedPlanAssignments, selectedRows],
  )
  const resolvedActors = useMemo(() => {
    const effectiveActors = (raidPlan.timeline.actors ?? []).map((actor) => {
      const replacementActor = getActorOverrideReplacementActor({
        actorOverrides,
        actorUuid: actor.uuid,
      })
      return replacementActor ?? actor
    })
    const actorResolverResult = resolveEventActivityTimelineActors({
      activityId,
      entries,
      actors: effectiveActors,
      autoMatchMode,
    })
    return actorResolverResult.resolvedActors.map((resolvedActor) => {
      const isIgnored = getActorOverrideIgnored({
        actorOverrides,
        actorUuid: resolvedActor.actor.uuid,
      })
      const overrideCharacterId = getActorOverrideCharacterId({
        actorOverrides,
        actorUuid: resolvedActor.actor.uuid,
      })
      const stickyCharacterId =
        stickyActorCharacterIdsByActorUuid?.[resolvedActor.actor.uuid]
      const overrideParticipant =
        typeof (overrideCharacterId ?? stickyCharacterId) === 'number'
          ? overrideParticipants.find(
              (participant) =>
                participant.characterId ===
                (overrideCharacterId ?? stickyCharacterId),
            )
          : undefined
      if (!overrideParticipant) {
        return { ...resolvedActor, isIgnored }
      }
      return {
        ...resolvedActor,
        participant: overrideParticipant,
        issue: undefined,
        isOverridden: typeof overrideCharacterId === 'number',
        isIgnored,
      }
    })
  }, [
    activityId,
    actorOverrides,
    autoMatchMode,
    entries,
    overrideParticipants,
    raidPlan.timeline.actors,
    stickyActorCharacterIdsByActorUuid,
  ])
  const participantIdentityByCharacterId = useMemo(
    () =>
      Object.fromEntries(
        (parsedParticipants ?? []).map((participant) => [
          getRaidlistParticipantCharacterId(participant.identity),
          participant.identity,
        ]),
      ),
    [parsedParticipants],
  )
  const resolvedActorIdentityMap = useMemo(
    () =>
      resolvedActors.reduce<Record<string, string>>((acc, resolvedActor) => {
        if (!resolvedActor.participant) return acc
        const participantIdentity =
          participantIdentityByCharacterId[
            resolvedActor.participant.characterId
          ]
        if (!participantIdentity) return acc
        acc[resolvedActor.actor.uuid] = participantIdentity
        return acc
      }, {}),
    [participantIdentityByCharacterId, resolvedActors],
  )
  useEffect(() => {
    onResolvedActorIdentityMapChange?.(resolvedActorIdentityMap)
  }, [onResolvedActorIdentityMapChange, resolvedActorIdentityMap])
  const castTargetResolverResult = useMemo(
    () =>
      resolveEventActivityCastTargets({
        activityId,
        entries,
        actors: resolvedActors.map((resolvedActor) => resolvedActor.actor),
        generalCasts: raidPlan.timeline.general?.casts ?? [],
        castTargetOverrides: getPlanCastTargetOverrides(
          planOverrides,
          raidPlan.id,
        ),
        autoMatchMode,
        overrideParticipants,
      }),
    [
      activityId,
      autoMatchMode,
      entries,
      overrideParticipants,
      planOverrides,
      raidPlan.id,
      raidPlan.timeline.general?.casts,
      resolvedActors,
    ],
  )
  const encounterAssignmentOverrides = useMemo(
    () => getPlanEncounterAssignmentOverrides(planOverrides, raidPlan.id),
    [planOverrides, raidPlan.id],
  )
  const resolvedByScopeBoxKey = useMemo(
    () =>
      applyEventActivityPlanOverrides({
        resolvedByScopeBoxKey: resolverResult.resolvedByScopeBoxKey,
        abilities: linkedPlanAssignments.map((entry) => entry.ability),
        encounterRows: selectedRows,
        selectedParticipants: overrideParticipants,
        encounterAssignmentOverrides,
      }),
    [
      encounterAssignmentOverrides,
      linkedPlanAssignments,
      overrideParticipants,
      resolverResult.resolvedByScopeBoxKey,
      selectedRows,
    ],
  )
  const sortedAbilityRows = useMemo(
    () =>
      linkedPlanAssignments
        .map((assignment) => ({
          abilityKey: assignment.abilityKey,
          row: assignment.row,
          ability: assignment.ability,
          isHidden: getEncounterAssignmentAbilityHidden({
            encounterAssignments: encounterAssignmentOverrides,
            abilityKey: assignment.abilityKey,
          }),
        }))
        .sort((left, right) => Number(left.isHidden) - Number(right.isHidden)),
    [encounterAssignmentOverrides, linkedPlanAssignments],
  )
  const hiddenAbilityKeys = useMemo(
    () =>
      new Set(
        sortedAbilityRows
          .filter((assignment) => assignment.isHidden)
          .map((assignment) => assignment.abilityKey),
      ),
    [sortedAbilityRows],
  )
  const unresolvedIssues = useMemo(
    () =>
      getEventActivityUnresolvedIssues(
        resolvedByScopeBoxKey,
        hiddenAbilityKeys,
      ),
    [hiddenAbilityKeys, resolvedByScopeBoxKey],
  )
  const unresolvedActorIssueCount = resolvedActors.filter(
    (resolvedActor) =>
      !resolvedActor.participant && resolvedActor.isIgnored !== true,
  ).length
  const linkedPlanIssueCount =
    unresolvedIssues.length +
    unresolvedActorIssueCount +
    castTargetResolverResult.issues.length
  const cooldownMaxTime = useMemo(
    () =>
      resolveLinkedPlanTimelineMaxTime({
        raidPlan,
        timeline: raidPlan.timeline,
      }),
    [raidPlan],
  )
  const isEmptyPreview =
    sortedAbilityRows.length === 0 && resolvedActors.length === 0
  const resolvedNote = useMemo(() => {
    const noteActivity = {
      id: activityId,
      difficulty: raidPlan.difficulty,
      encounter,
    } as unknown as wowaudit.EventActivity
    return buildEventActivityLinkedPlanNote({
      activity: noteActivity,
      linkedPlanData: {
        raidPlans: [
          {
            linkedRaidPlan: raidPlan,
            phaseDividers,
            linkedPlanAssignments,
            selectedRows,
            resolvedAssignmentAbilitiesByCharacterId: {},
            resolvedActors,
            resolverResult: {
              resolvedByScopeBoxKey,
              issues: unresolvedIssues,
            },
            actorResolverResult: {
              resolvedActors,
              issues: [],
            },
            castTargetResolverResult,
            linkedPlanIssueCount,
            hasLinkedPlanIssues: linkedPlanIssueCount > 0,
          },
        ],
        linkedVisualPlans: [],
        linkedPlans: [],
        selectedParticipants,
        linkedPlanIssueCount,
        hasLinkedPlanIssues: linkedPlanIssueCount > 0,
      },
      allEntries: entries,
      planAbilityDefaults,
    })
  }, [
    activityId,
    castTargetResolverResult,
    encounter,
    entries,
    linkedPlanAssignments,
    linkedPlanIssueCount,
    phaseDividers,
    raidPlan,
    resolvedActors,
    resolvedByScopeBoxKey,
    selectedParticipants,
    selectedRows,
    unresolvedIssues,
    planAbilityDefaults,
  ])
  useEffect(() => {
    onResolvedNoteChange?.(resolvedNote)
  }, [onResolvedNoteChange, resolvedNote])

  return (
    <TeamContext.Provider
      value={{
        guild: {} as wowaudit.Guild,
        team: {
          id: owner.ownerType === 'team' ? owner.ownerId : 0,
          path: planListPathPrefix,
          members: teamMemberships,
          planAbilityDefaults: planAbilityDefaults ?? {},
        } as wowaudit.Team,
        refetchTeamDetails: () => null,
      }}
    >
      <Card className='p-4'>
        {linkedPlanIssueCount > 0 ? (
          <Alert variant='warning' className='pb-2 mb-4'>
            <TriangleAlert className='h-4 w-4' />
            <AlertTitle>Issues to resolve: {linkedPlanIssueCount}</AlertTitle>
            <AlertDescription>
              You can resolve issues directly in this note by clicking on them.
              Unresolved entries will be omitted from the note when copied.
            </AlertDescription>
          </Alert>
        ) : null}
        {isEmptyPreview ? (
          <p className='text-sm text-muted-foreground text-center py-8'>
            This raid plan is empty
          </p>
        ) : (
          <OverridePopoverLayerContext.Provider value={true}>
            <ResolvedPlanPreviewContent
              raidPlan={raidPlan}
              planListPathPrefix={planListPathPrefix}
              rosterMemberships={teamMemberships}
              planOverrides={planOverrides}
              onPlanOverridesChange={onPlanOverridesChange}
              sortedAbilityRows={sortedAbilityRows}
              selectedRows={selectedRows}
              phaseDividers={phaseDividers}
              selectedParticipants={selectedParticipants}
              overrideParticipants={overrideParticipants}
              resolvedByScopeBoxKey={resolvedByScopeBoxKey}
              resolvedActors={resolvedActors}
              resolvedCastTargetsByCastId={
                castTargetResolverResult.resolvedByCastId
              }
              cooldownMaxTime={cooldownMaxTime}
              canEdit={true}
              isSaving={false}
              allowUnresolvedActorOverride={allowUnresolvedActorOverride}
              onActorPreferredCharacterChange={onActorPreferredCharacterChange}
            />
          </OverridePopoverLayerContext.Provider>
        )}
      </Card>
    </TeamContext.Provider>
  )
}

export default CreateNotePreview
